import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetProductDetailDocument,
  UpdateProductDocument,
  type GetProductDetailQuery,
  type ProductType,
  type UpdateProductMutation,
  type UpdateProductMutationVariables,
  // type DeleteProductMutation,
  // type DeleteProductMutationVariables,
} from '@/generated/root-types';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { useNotification } from '@/app/contexts/NotificationContext';
import { errorsFromPayload } from '@/utils/mutationErrors';
import { productTypeLabel } from '@/utils/productTypeLabel';
import {
  detailBackButtonSx,
  detailFieldGridSx,
  detailFieldSx,
  detailFullRowSx,
  detailHeaderRowSx,
  detailPageContainerSx,
  detailTitleSx,
} from '@/utils/detailPageLayout';

const PRODUCT_TYPES: ProductType[] = ['SERVICE', 'LICENSE', 'PHYSICAL', 'DIGITAL'];

export default function ProductDetail() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const superAdmin = isSuperAdmin(user);

  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const [type, setType] = useState<ProductType>('SERVICE');
  const [sort, setSort] = useState('0');
  const [sku, setSku] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [longDescription, setLongDescription] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery<GetProductDetailQuery>(
    GetProductDetailDocument,
    {
      variables: { id: id! },
      skip: !id,
    },
  );

  const product = data?.product;

  useEffect(() => {
    if (!product) return;
    setName(product.name);
    setNumber(product.number);
    setType(product.type);
    setSort(String(product.sort));
    setSku(product.sku ?? '');
    setShortDescription(product.shortDescription ?? '');
    setLongDescription(product.longDescription ?? '');
  }, [product]);

  const [updateProduct, { loading: saving }] = useMutation<
    UpdateProductMutation,
    UpdateProductMutationVariables
  >(UpdateProductDocument);

  // const [deleteProduct, { loading: deleting }] = useMutation<
  //   DeleteProductMutation,
  //   DeleteProductMutationVariables
  // >(DeleteProductDocument);

  const handleSave = async () => {
    if (!id) return;
    const s = parseInt(sort, 10);
    if (Number.isNaN(s)) {
      showError(t('errors.generic'));
      return;
    }
    try {
      const res = await updateProduct({
        variables: {
          input: {
            id,
            name,
            number,
            type,
            sort: s,
            sku: sku || undefined,
            shortDescription: shortDescription || undefined,
            longDescription: longDescription || undefined,
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const pe = errorsFromPayload(res.data?.updateProduct);
      if (pe) {
        showError(pe);
        return;
      }
      showSuccess(t('common.saved'));
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  // const handleDelete = async () => {
  //   if (!id) return;
  //   try {
  //     //const res = await deleteProduct({ variables: { input: { id } } });
  //     const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
  //     if (top?.length) {
  //       showError(top.map((e) => e.message).join(' '));
  //       return;
  //     }
  //     const pe = errorsFromPayload(res.data?.deleteProduct);
  //     if (pe || !res.data?.deleteProduct?.boolean) {
  //       showError(pe || t('errors.generic'));
  //       return;
  //     }
  //     showSuccess(t('common.deleted'));
  //     navigate('/products');
  //   } catch (e) {
  //     showError(e instanceof Error ? e.message : t('errors.generic'));
  //   }
  // };

  if (loading) {
    return (
      <Box sx={{ ...detailPageContainerSx, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box sx={detailPageContainerSx}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/products')}>
          {t('common.back')}
        </Button>
        <Typography color="error" sx={{ mt: 2 }}>
          {error?.message ?? t('errors.generic')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={detailPageContainerSx}>
      <Box sx={detailHeaderRowSx}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/products')}
          sx={detailBackButtonSx}
        >
          {t('common.back')}
        </Button>
        <Typography variant="h4" component="h1" sx={detailTitleSx}>
          {product.name}
        </Typography>
      </Box>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, width: '100%', boxSizing: 'border-box' }}
      >
        <Box sx={detailFieldGridSx}>
          <TextField
            sx={detailFieldSx}
            label={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            disabled={!superAdmin}
          />
          <TextField
            sx={detailFieldSx}
            label={t('masterData.sku')}
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            fullWidth
            disabled={!superAdmin}
          />
          <TextField
            sx={detailFieldSx}
            label={t('masterData.productNumber')}
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            fullWidth
            disabled={!superAdmin}
          />
          <TextField
            sx={detailFieldSx}
            select
            label={t('common.type')}
            value={type}
            onChange={(e) => setType(e.target.value as ProductType)}
            fullWidth
            disabled={!superAdmin}
          >
            {PRODUCT_TYPES.map((x) => (
              <MenuItem key={x} value={x}>
                {productTypeLabel(t, x)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            sx={detailFieldSx}
            label={t('masterData.sort')}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            type="number"
            fullWidth
            disabled={!superAdmin}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.created')}
            value={formatDateTime(product.createdAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.updated')}
            value={formatDateTime(product.updatedAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={{ ...detailFieldSx, ...detailFullRowSx }}
            label={t('masterData.shortDescription')}
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            fullWidth
            multiline
            disabled={!superAdmin}
          />
          <TextField
            sx={{ ...detailFieldSx, ...detailFullRowSx }}
            label={t('masterData.longDescription')}
            value={longDescription}
            onChange={(e) => setLongDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            disabled={!superAdmin}
          />
          {superAdmin && (
            <Box sx={{ ...detailFullRowSx, display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {t('common.save')}
              </Button>
              <Button color="error" onClick={() => setDeleteOpen(true)}>
                {t('common.delete')}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t('masterData.confirmDeleteProduct')}</DialogTitle>
        <DialogContent>
          <Typography>{t('masterData.confirmDeleteBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
          {/* <Button color="error" onClick={handleDelete} disabled={deleting}>
            {t('common.delete')}
          </Button> */}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
